import { useEffect, useState, useRef, useCallback } from 'react';
import { loadGoogleMapsLibrary } from '@/lib/google-maps-loader';

export interface RouteInfo {
  distance: number; // Distance in meters
  duration: number; // Duration in seconds
  durationInTraffic?: number; // Duration in seconds considering traffic
  polyline?: string; // Encoded polyline for the route
  steps?: Array<{
    distance: number;
    duration: number;
    instruction: string;
  }>;
}

export interface RouteCalculationResult {
  route: RouteInfo | null;
  eta: Date | null; // Estimated time of arrival
  etaMinutes: number | null; // Estimated minutes until arrival
  loading: boolean;
  error: Error | null;
}

interface UseRouteCalculationOptions {
  origin: { lat: number; lng: number } | null;
  destination: { lat: number; lng: number } | null;
  enabled?: boolean;
  travelMode?: 'DRIVING' | 'WALKING' | 'BICYCLING' | 'TRANSIT';
  avoidTolls?: boolean;
  avoidHighways?: boolean;
  cacheDuration?: number; // Cache results for this many milliseconds (default: 30000 = 30 seconds)
  onRouteCalculated?: (result: RouteCalculationResult) => void;
}

/**
 * Hook to calculate route and ETA using Google Maps Routes API
 * Uses the new Routes API (not the deprecated Directions API)
 */
export function useRouteCalculation({
  origin,
  destination,
  enabled = true,
  travelMode = 'DRIVING',
  avoidTolls = false,
  avoidHighways = false,
  cacheDuration = 30000, // 30 seconds default cache
  onRouteCalculated,
}: UseRouteCalculationOptions): RouteCalculationResult {
  const [route, setRoute] = useState<RouteInfo | null>(null);
  const [eta, setEta] = useState<Date | null>(null);
  const [etaMinutes, setEtaMinutes] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const cacheRef = useRef<{
    key: string;
    result: RouteCalculationResult;
    timestamp: number;
  } | null>(null);

  // Calculate distance between two points (Haversine formula)
  const calculateDistance = useCallback((lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lng2 - lng1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  }, []);

  const calculateRoute = useCallback(async () => {
    if (!enabled || !origin || !destination) {
      setRoute(null);
      setEta(null);
      setEtaMinutes(null);
      setError(null);
      return;
    }

    // Validate distance - reject if too far (e.g., > 500km)
    const distanceMeters = calculateDistance(origin.lat, origin.lng, destination.lat, destination.lng);
    const MAX_DISTANCE_METERS = 500000; // 500km
    if (distanceMeters > MAX_DISTANCE_METERS) {
      const distanceKm = Math.round(distanceMeters / 1000);
      const error = new Error(`Distância muito grande (${distanceKm}km). Rota não pode ser calculada para distâncias maiores que 500km.`);
      console.warn('Route calculation skipped - distance too large:', distanceKm, 'km');
      setError(error);
      setRoute(null);
      setEta(null);
      setEtaMinutes(null);
      setLoading(false);
      
      // Cache the error result to avoid recalculating
      const errorCacheKey = `${origin.lat},${origin.lng}-${destination.lat},${destination.lng}-${travelMode}`;
      const errorResult: RouteCalculationResult = {
        route: null,
        eta: null,
        etaMinutes: null,
        loading: false,
        error,
      };
      cacheRef.current = {
        key: errorCacheKey,
        result: errorResult,
        timestamp: Date.now(),
      };
      
      if (onRouteCalculated) {
        onRouteCalculated(errorResult);
      }
      return;
    }

    // Check cache
    const cacheKey = `${origin.lat},${origin.lng}-${destination.lat},${destination.lng}-${travelMode}`;
    if (cacheRef.current && cacheRef.current.key === cacheKey) {
      const age = Date.now() - cacheRef.current.timestamp;
      if (age < cacheDuration) {
        // Use cached result
        const cached = cacheRef.current.result;
        setRoute(cached.route);
        setEta(cached.eta);
        setEtaMinutes(cached.etaMinutes);
        setError(cached.error);
        if (onRouteCalculated) {
          onRouteCalculated(cached);
        }
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      // Load maps library (Directions API is included in maps library)
      await loadGoogleMapsLibrary('maps');

      // Wait for Google Maps to be available
      let retries = 0;
      while (!window.google?.maps?.DirectionsService && retries < 20) {
        await new Promise(resolve => setTimeout(resolve, 100));
        retries++;
      }

      if (!window.google?.maps?.DirectionsService) {
        throw new Error('Google Maps Directions API not available');
      }

      // Use Directions API (more reliable and widely supported)
      const directionsService = new window.google.maps.DirectionsService();

      // Prepare Directions API request
      const request: google.maps.DirectionsRequest = {
        origin: new window.google.maps.LatLng(origin.lat, origin.lng),
        destination: new window.google.maps.LatLng(destination.lat, destination.lng),
        travelMode: window.google.maps.TravelMode[travelMode] || window.google.maps.TravelMode.DRIVING,
        avoidTolls: avoidTolls,
        avoidHighways: avoidHighways,
        unitSystem: window.google.maps.UnitSystem.METRIC,
      };

      // Calculate route using Directions API
      const response = await new Promise<google.maps.DirectionsResult>((resolve, reject) => {
        directionsService.route(request, (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK && result) {
            resolve(result);
          } else {
            reject(new Error(`Directions API error: ${status}`));
          }
        });
      });

      if (!response || !response.routes || response.routes.length === 0) {
        throw new Error('No route found');
      }

      const routeData = response.routes[0];
      const leg = routeData.legs?.[0];

      if (!leg) {
        throw new Error('No route leg found');
      }

      // Extract route information from Directions API response
      const routeInfo: RouteInfo = {
        distance: leg.distance?.value || 0, // Directions API uses distance.value (in meters)
        duration: leg.duration?.value || 0, // Directions API uses duration.value (in seconds)
        durationInTraffic: leg.duration_in_traffic?.value || leg.duration?.value || 0,
        polyline: routeData.overview_polyline?.points, // Directions API uses overview_polyline.points
        steps: leg.steps?.map((step) => ({
          distance: step.distance?.value || 0,
          duration: step.duration?.value || 0,
          instruction: step.instructions || '',
        })),
      };

      // Calculate ETA
      const durationSeconds = routeInfo.durationInTraffic || routeInfo.duration;
      const etaDate = new Date(Date.now() + durationSeconds * 1000);
      const etaMins = Math.ceil(durationSeconds / 60);

      setRoute(routeInfo);
      setEta(etaDate);
      setEtaMinutes(etaMins);

      const result: RouteCalculationResult = {
        route: routeInfo,
        eta: etaDate,
        etaMinutes: etaMins,
        loading: false,
        error: null,
      };

      // Update cache
      cacheRef.current = {
        key: cacheKey,
        result,
        timestamp: Date.now(),
      };

      if (onRouteCalculated) {
        onRouteCalculated(result);
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to calculate route');
      console.error('Error calculating route:', error);
      setError(error);
      setRoute(null);
      setEta(null);
      setEtaMinutes(null);

      const result: RouteCalculationResult = {
        route: null,
        eta: null,
        etaMinutes: null,
        loading: false,
        error,
      };

      if (onRouteCalculated) {
        onRouteCalculated(result);
      }
    } finally {
      setLoading(false);
    }
  }, [
    enabled,
    origin,
    destination,
    travelMode,
    avoidTolls,
    avoidHighways,
    cacheDuration,
    onRouteCalculated,
    calculateDistance,
  ]);

  // Recalculate when inputs change (with debounce to avoid loops)
  useEffect(() => {
    if (!enabled || !origin || !destination) {
      return;
    }

    // Debounce to avoid rapid recalculations
    const timeoutId = setTimeout(() => {
      calculateRoute();
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [enabled, origin?.lat, origin?.lng, destination?.lat, destination?.lng, travelMode, calculateRoute]);

  return {
    route,
    eta,
    etaMinutes,
    loading,
    error,
  };
}

